import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, Map } from 'lucide-react';
import { cities } from '../../data/cities';
import { states } from '../../data/states';

// Pre-compute static derived lists once (module scope) — no per-render cost.
const MICHIGAN_CITIES = cities.filter((c) => c.stateAbbr === 'MI');
const STATE_CITY_COUNTS = states.reduce((acc, s) => {
  acc[s.stateAbbr] = cities.filter((c) => c.stateAbbr === s.stateAbbr).length;
  return acc;
}, {});

const SectionHeading = () => (
  <div className="text-center mb-14">
    <span className="inline-block px-4 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-4">
      Markets We Serve
    </span>
    <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
      We Buy Houses Across America's Top Wholesaling Markets
    </h2>
    <p className="text-lg text-slate-600 max-w-2xl mx-auto">
      From Detroit row homes to Phoenix flips to LA probate deals — fast cash offers for sellers and exclusive off-market deals for investors.
    </p>
  </div>
);

const BrowseByState = () => (
  <div className="mb-12">
    <div className="flex items-center gap-2 mb-4">
      <Map className="w-5 h-5 text-emerald-600" />
      <h3 className="text-lg font-bold text-slate-900">Browse by State</h3>
    </div>
    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
      {states.map((s) => (
        <Link
          key={s.slug}
          to={`/we-buy-houses/${s.slug}`}
          data-testid={`state-hub-${s.slug}`}
          className="group p-5 rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 hover:border-emerald-500 hover:shadow-md transition-all"
        >
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-xl font-bold text-slate-900 group-hover:text-emerald-700 transition-colors">
                We Buy Houses {s.name}
              </h4>
              <p className="text-sm text-slate-600 mt-1">
                {STATE_CITY_COUNTS[s.stateAbbr]} cities • Statewide coverage
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-emerald-600 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>
      ))}
    </div>
  </div>
);

const FeaturedMichigan = () => (
  <div className="mb-12 p-6 md:p-8 rounded-2xl bg-gradient-to-br from-emerald-50 to-amber-50 border border-emerald-100">
    <div className="flex items-center gap-2 mb-2">
      <span className="text-xs uppercase tracking-wider font-bold text-emerald-700">Featured Market</span>
      <span className="h-px flex-1 bg-emerald-200" />
    </div>
    <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Michigan — Detroit Metro</h3>
    <p className="text-slate-700 mb-5 max-w-3xl">
      Detroit is America's #1 wholesaling market. We move fast on properties across Wayne, Oakland, Macomb, and Genesee counties — from Brightmoor to Bloomfield Hills.
    </p>
    <div className="flex flex-wrap gap-2">
      <Link
        to="/we-buy-houses/michigan"
        data-testid="featured-mi-state"
        className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 rounded-full text-sm font-semibold text-white hover:bg-emerald-700 transition-colors"
      >
        See all Michigan cities →
      </Link>
      {MICHIGAN_CITIES.map((c) => (
        <Link
          key={c.slug}
          to={`/we-buy-houses/${c.slug}`}
          data-testid={`market-link-${c.slug}`}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-white rounded-full text-sm font-semibold text-slate-800 hover:bg-emerald-600 hover:text-white border border-emerald-200 transition-colors"
        >
          <MapPin className="w-3.5 h-3.5" />
          {c.name}, {c.stateAbbr}
        </Link>
      ))}
    </div>
  </div>
);

const CityGrid = () => (
  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
    {cities.map((c) => (
      <Link
        key={c.slug}
        to={`/we-buy-houses/${c.slug}`}
        data-testid={`city-card-${c.slug}`}
        className="group p-5 rounded-xl border border-slate-200 hover:border-emerald-400 hover:shadow-md transition-all bg-white"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-slate-500">{c.state}</span>
            </div>
            <h3 className="text-lg font-bold text-slate-900 group-hover:text-emerald-600 transition-colors">
              We Buy Houses in {c.name}
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              Cash offers, any condition. Investors: off-market deals.
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all flex-shrink-0 ml-2" />
        </div>
      </Link>
    ))}
  </div>
);

const MarketsSection = () => (
  <section id="markets" className="py-20 bg-white">
    <div className="container mx-auto px-6">
      <SectionHeading />
      <BrowseByState />
      <FeaturedMichigan />
      <CityGrid />
      <p className="text-center text-sm text-slate-500 mt-10">
        Don't see your city? We buy houses <strong>nationwide</strong> — submit your property above for a cash offer.
      </p>
    </div>
  </section>
);

export default MarketsSection;
